import React, { useRef, useState } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, List, ListItem, ListItemText, ListItemIcon } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { authFetch } from '../utils/authFetch';
import PropTypes from "prop-types";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LapControls({ laps, onDownload, onUpload }) {
  const fileInputRef = useRef();
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [lapsToDownload, setLapsToDownload] = useState([]);
  const [lapList, setLapList] = useState(laps);

  // Sync lapList with laps prop
  React.useEffect(() => {
    setLapList(laps);
  }, [laps]);

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    onUpload(files);
    e.target.value = "";
  };

  // Always fetch latest laps when opening the download dialog
  const handleOpenDownloadDialog = () => {
    setLapsToDownload([]);
    authFetch(`${BACKEND_URL}/laps/`)
      .then(res => res && res.ok ? res.json() : null)
      .then(data => {
        if (data) setLapList([...data].sort());
        else setLapList([...laps].sort()); // fallback
        setDownloadDialogOpen(true);
      })
      .catch(() => {
        setLapList([...laps].sort()); // fallback
        setDownloadDialogOpen(true);
      });
  };

  const handleCloseDownloadDialog = () => {
    setDownloadDialogOpen(false);
  };

  const handleToggleLap = (lapId) => {
    setLapsToDownload(prev =>
      prev.includes(lapId) ? prev.filter(id => id !== lapId) : [...prev, lapId]
    );
  };

  const handleDownloadConfirm = () => {
    onDownload(lapsToDownload);
    setDownloadDialogOpen(false);
  };

  return (
    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<CloudDownloadIcon />}
        onClick={handleOpenDownloadDialog}
        aria-label="Download laps"
      >
        Download
      </Button>
      <Dialog open={downloadDialogOpen} onClose={handleCloseDownloadDialog}>
        <DialogTitle>Select Laps to Download</DialogTitle>
        <DialogContent>
          <List>
            <ListItem button onClick={() => {
              if (lapsToDownload.length === lapList.length) {
                setLapsToDownload([]); // Deselect all
              } else {
                setLapsToDownload(lapList); // Select all
              }
            }}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={lapsToDownload.length === lapList.length && lapList.length > 0}
                  indeterminate={lapsToDownload.length > 0 && lapsToDownload.length < lapList.length}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-label': 'Select all laps' }}
                />
              </ListItemIcon>
              <ListItemText primary="Select All" />
            </ListItem>
            {lapList.map(lap => (
              <ListItem key={lap} button onClick={() => handleToggleLap(lap)}>
                <ListItemIcon>
                  <Checkbox edge="start" checked={lapsToDownload.includes(lap)} tabIndex={-1} disableRipple inputProps={{ 'aria-label': `Select lap ${lap}` }} />
                </ListItemIcon>
                <ListItemText primary={lap} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDownloadDialog} aria-label="Cancel download">Cancel</Button>
          <Button onClick={handleDownloadConfirm} variant="contained" aria-label="Confirm download">Download</Button>
        </DialogActions>
      </Dialog>
      <input
        type="file"
        accept="application/json"
        multiple
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        aria-label="Upload laps"
      />
      <Button
        variant="contained"
        color="secondary"
        startIcon={<CloudUploadIcon />}
        onClick={handleUploadClick}
        aria-label="Upload laps"
      >
        Upload
      </Button>
    </Box>
  );
}

LapControls.propTypes = {
  laps: PropTypes.array.isRequired,
  onDownload: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired
};
